import React, { useCallback, useMemo } from "react";
import {
    buildInvoiceRefLabel,
    InvoiceReference,
} from "@common/schemas/invoiceReference.schema";
import { InvoicingIcon } from "@icons";
import { useOverlays } from "contexts/OverlayContext";
import { useEntities } from "@hooks/useEntities";
import { useInvoiceRefContextMaybe } from "@components/tasks/InvoiceRefContext";
import { RefChip, RefChipProps } from "./RefChip";
import { useRefChipHoverFocus } from "./useRefChipHoverFocus";

interface InvoiceRefChipProps extends Omit<RefChipProps, "icon" | "label"> {
    reference: InvoiceReference;
}

export const InvoiceRefChip: React.FC<InvoiceRefChipProps> = React.memo(
    function InvoiceRefChip({ reference, ...handlers }) {
        return (
            <RefChip
                icon={InvoicingIcon}
                label={reference.label ?? buildInvoiceRefLabel(reference)}
                {...handlers}
            />
        );
    },
);

/**
 * Chip in a sent message: hover highlights the invoice row in the task table;
 * click opens the invoice details drawer.
 */
export const MessageInvoiceRefChip: React.FC<{
    reference: InvoiceReference;
}> = React.memo(function MessageInvoiceRefChip({ reference }) {
    const { openOverlay } = useOverlays();
    const entities = useEntities();
    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const invoiceRefCtx = useInvoiceRefContextMaybe();
    const { handleMouseEnter, handleMouseLeave } = useRefChipHoverFocus({
        id: reference.invoiceId,
        setFocusedId: invoiceRefCtx?.setFocusedInvoiceId,
    });

    const handleClick = useCallback(() => {
        openOverlay("invoiceLineItemsDrilldown", {
            invoiceId: reference.invoiceId,
            entityIds,
        });
    }, [openOverlay, reference.invoiceId, entityIds]);

    return (
        <InvoiceRefChip
            reference={reference}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        />
    );
});
